import axios from "axios";
import { toast } from "sonner";
import { AlertCircle, ExternalLink, Loader2 } from "lucide-react";
import { useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─────────────────────────────────────────────────────────────────────────────
// Billing Issue Banner — shows during Apple's / Stripe's grace period when
// a renewal charge has failed. Catches lapsed cards BEFORE the subscription
// actually expires, which is the window with the highest save-rate for
// subscription apps.
//
// Triggered by RevenueCat's BILLING_ISSUE event setting `billing_issue:true`
// on the user. Any successful subsequent event (RENEWAL / UNCANCELLATION /
// PRODUCT_CHANGE) clears the flag.
//
// Link target depends on the purchase source:
//   - iOS (RevenueCat / StoreKit)   → apps.apple.com/account/billing
//   - Web (Stripe)                  → POST /api/billing/customer-portal
//     redirects into the Stripe-hosted billing portal so the user can swap
//     the card without leaving our backend's server-of-truth path.
//
// Props:
//   billing — the /api/billing/status response, must include
//     { billing_issue: boolean, source: "ios_rc" | "web_stripe", ... }
// ─────────────────────────────────────────────────────────────────────────────

export const BillingIssueBanner = ({ billing }) => {
  const [portalLoading, setPortalLoading] = useState(false);
  if (!billing?.billing_issue) return null;

  const isIOS = billing.source === "ios_rc";
  const iosHref = "https://apps.apple.com/account/billing";

  const openWebPortal = async (e) => {
    e.preventDefault();
    setPortalLoading(true);
    try {
      const res = await axios.post(
        `${API}/billing/customer-portal`,
        { return_url: `${window.location.origin}/dashboard` },
        { withCredentials: true }
      );
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error("Couldn't open billing portal. Please try /pricing.");
    } catch (err) {
      // 404 no_stripe_customer → user never completed a web purchase, so fall
      // back to /pricing where they can start a fresh subscription.
      if (err?.response?.status === 404) {
        window.location.href = "/pricing";
        return;
      }
      toast.error("Couldn't open billing portal. Please try again.");
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <div
      className="border-b"
      style={{ backgroundColor: "#FEF2F0", borderBottomColor: "#D9888033" }}
      data-testid="billing-issue-banner"
    >
      <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center gap-3 text-sm">
        <AlertCircle
          className="w-4 h-4 shrink-0"
          style={{ color: "#D98880" }}
          aria-hidden
        />
        <p className="flex-1 truncate" style={{ color: "#8C4A42" }}>
          <strong className="font-semibold mr-1.5">Your last payment failed.</strong>
          <span className="hidden sm:inline">Update your billing details to keep your subscription active. </span>
          {isIOS ? (
            <a
              href={iosHref}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 font-medium hover:opacity-80 inline-flex items-center gap-1"
              data-testid="billing-issue-cta"
            >
              Update billing in App Store
              <ExternalLink className="w-3 h-3" />
            </a>
          ) : (
            <button
              onClick={openWebPortal}
              disabled={portalLoading}
              className="underline underline-offset-2 font-medium hover:opacity-80 inline-flex items-center gap-1 disabled:opacity-60"
              data-testid="billing-issue-cta"
            >
              {portalLoading ? (
                <><Loader2 className="w-3 h-3 animate-spin" /> Opening…</>
              ) : (
                <>Update billing<ExternalLink className="w-3 h-3" /></>
              )}
            </button>
          )}
        </p>
      </div>
    </div>
  );
};

export default BillingIssueBanner;
